import React, { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { FEATURES } from '../constants/features';
import {
  getFishIQDisplayImage,
  resolveFishIQSpeciesIdentity,
} from '../services/fishIQVisualReference';
import type { FishIQSpeciesImage as FishIQSpeciesImageMeta } from '../types';

interface FishIQSpeciesImageProps {
  species?: any;
  image?: FishIQSpeciesImageMeta | null;
  fallback?: React.ReactNode;
  className?: string;
  imageClassName?: string;
  alt?: string;
  size?: 'thumb' | 'card' | 'hero';
  showCredit?: boolean;
  isDarkMode?: boolean;
}

const sourceForSize = (image: FishIQSpeciesImageMeta, size?: string) => {
  if (size === 'thumb') return image.thumbnailUrl || image.cardUrl || image.url;
  if (size === 'card') return image.cardUrl || image.thumbnailUrl || image.url;
  return image.heroUrl || image.url || image.cardUrl || image.thumbnailUrl || '';
};

const sourceOriginalFromNotes = (image: FishIQSpeciesImageMeta) => {
  const notes = String(image.notes || '');
  const marker = 'Source original:';
  const start = notes.indexOf(marker);
  if (start < 0) return '';

  const raw = notes.slice(start + marker.length).trim();
  const match = raw.match(/^https?:\/\/\S+/i);
  if (!match?.[0]) return '';

  return match[0].replace(/[),.;]+$/, '');
};

const uniqueSources = (...sources: Array<string | null | undefined>) =>
  Array.from(new Set(sources.map((value) => String(value || '').trim()).filter(Boolean)));

const FishIQSpeciesImage: React.FC<FishIQSpeciesImageProps> = ({
  species,
  image: imageProp,
  fallback = '🐟',
  className = '',
  imageClassName = '',
  alt,
  size = 'card',
  showCredit = false,
  isDarkMode = false,
}) => {
  const [sourceIndex, setSourceIndex] = useState(0);

  const resolvedImage = useMemo(() => {
    if (!(FEATURES as any).FISHIQ_VISUAL_REFERENCES) return null;
    if (imageProp) return imageProp;
    const identity = resolveFishIQSpeciesIdentity(species);
    return getFishIQDisplayImage(identity.canonicalSpeciesId, identity.scientificName);
  }, [imageProp, species]);

  const sources = useMemo(() => {
    if (!resolvedImage) return [];

    const roddoAsset = sourceForSize(resolvedImage, size);
    const original = sourceOriginalFromNotes(resolvedImage);
    const isNativeIOS =
      Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

    return isNativeIOS
      ? uniqueSources(original, roddoAsset)
      : uniqueSources(roddoAsset, original);
  }, [resolvedImage, size]);

  useEffect(() => {
    setSourceIndex(0);
  }, [resolvedImage?.id, size]);

  const src = sources[sourceIndex] || '';
  const failed = sourceIndex >= sources.length;
  const commonName = String(species?.commonName || species?.name || '').trim();

  if (!resolvedImage || !src || failed) {
    return (
      <div
        className={`grid place-items-center ${className}`}
        aria-label={commonName || 'Fish reference'}
      >
        {fallback}
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <img
        src={src}
        alt={alt || `${commonName || resolvedImage.scientificName} reference`}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setSourceIndex((current) => current + 1)}
        className={`h-full w-full object-cover ${imageClassName}`}
      />

      {showCredit ? (
        <div
          className="absolute inset-x-0 bottom-0 px-2 py-1 text-[9px] font-semibold leading-4"
          style={{
            background: isDarkMode
              ? 'linear-gradient(180deg, transparent, rgba(7,11,21,0.90))'
              : 'linear-gradient(180deg, transparent, rgba(37,31,24,0.78))',
            color: '#fff',
          }}
        >
          {resolvedImage.creditText}
        </div>
      ) : null}
    </div>
  );
};

export default FishIQSpeciesImage;
